import { Outlet, useLocation } from "react-router-dom";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";
import { Suspense, useState, useEffect } from "react";
import { isMobileDevice } from "@/lib/layout/mobileDetection";
import { useSidebar } from "@/contexts/SidebarContext";
import { useUpdateNotification } from "@/hooks/useUpdateNotification";
import { Toaster } from "sonner";
import { AiChatWidgetV2 } from "@/components/chat/AiChatWidgetV2";
import { NotificationPermissionBanner } from "@/components/notifications/NotificationPermissionBanner";
import { FollowedProjectsModal } from "@/components/notifications/FollowedProjectsModal";
import { UpdateFloatingCard } from "@/components/update-notification/UpdateFloatingCard";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { usePermissions } from "@/contexts/PermissionContext";
import { CommissionDestinationSkeleton } from "@/components/commission/CommissionDestinationSkeleton";
import { OutletDestinationSkeleton } from "@/components/layout/OutletDestinationSkeleton";
import { PageLoadingSpinner, SectionLoadingSpinner } from "@/components/ui/LoadingSpinner";
import { outletSkeletonForPath } from "@/lib/outletSkeleton";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { WorkspaceUnavailable } from "./WorkspaceUnavailable";
import { SESSION_BOOT_WATCHDOG_MS } from "@/lib/sessionBoot";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { SlidePanel } from "@/components/ui/slide-panel";
import AddUserPage from "@/pages/AddUserPage";

/*
 * NAV-4.2: the mobile-detection vote moved to `@/lib/layout/mobileDetection` so its rule is unit-testable
 * — nothing inside a component is, since vitest here runs `environment: 'node'` with no jsdom. Behaviour
 * is unchanged: the same four indicators, the same 2-of-4 threshold, the same OR across screen dimensions.
 */

export function MainLayout() {
  const { sidebarOpen, sidebarCollapsed, toggleSidebar, toggleSidebarCollapse } = useSidebar();
  const [isAddUserPanelOpen, setIsAddUserPanelOpen] = useState(false);

  /**
   * NAV-4.2. `isMobile` answers exactly one question: is `100vh` lying?
   *
   * It was `useState(isMobileDevice())` with `setIsMobile` NEVER CALLED — seeded once at mount and never
   * updated, which ESLint had been reporting as an unused setter. It now updates alongside `--vh`.
   *
   * ⚠️ IT IS NOT A LAYOUT FLAG. Whether the sidebar should overlay the content is a VIEWPORT question, and
   * the `md:` breakpoints answer that in CSS where nothing can go stale — see the backdrops below, which no
   * longer consult this.
   */
  const [isMobile, setIsMobile] = useState(isMobileDevice);

  const location = useLocation();
  const outletSkeleton = outletSkeletonForPath(location.pathname);

  /**
   * Keep `--vh` (and the mobile verdict) in step with the viewport.
   *
   * Mobile browsers collapse their address bar, so `100vh` measures more than the user can see; `--vh` is
   * one hundredth of the real `innerHeight` and the styles below multiply it back up.
   *
   * ⚠️ EMPTY DEPS, DELIBERATELY. This used to depend on `[isMobile]`, which was harmless only because the
   * flag never changed — now that it does, that dependency would tear down and re-add the listeners on
   * every flip. The handler reads nothing from the render scope, so it never needs rebinding.
   *
   * The previous version also `console.log`ed the full user agent and every detection signal on EVERY
   * resize event, which fires continuously through a window drag. Removed: this effect is now the hot path
   * for a resize and nothing should be serialising a UA string in it.
   */
  useEffect(() => {
    const syncViewport = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
      // Cheap: a handful of navigator/screen reads. React bails out when the value is unchanged, so a
      // desktop resize does not re-render.
      setIsMobile(isMobileDevice());
    };

    syncViewport();
    window.addEventListener('resize', syncViewport);
    window.addEventListener('orientationchange', syncViewport);

    return () => {
      window.removeEventListener('resize', syncViewport);
      window.removeEventListener('orientationchange', syncViewport);
    };
  }, []);

  // Update notification system
  const { showNotification, handleRefresh, handleDismiss } = useUpdateNotification({
    enabled: true, // Enable update checking
  });

  // Get loading states from all contexts
  const {
    user,
    loading: authLoading,
    isImpersonating,
    originalUser,
  } = useAuth();
  const { currentTenant, isLoading: tenantLoading } = useTenant();
  const { loading: permissionLoading, hasPermission } = usePermissions();

  // Check if all contexts are still loading
  const isContextLoading =
    authLoading ||
    tenantLoading ||
    permissionLoading ||
    !user ||
    !currentTenant;

  useEffect(() => {}, [isImpersonating, user, originalUser]);

  /**
   * ⚠️ THE LAST BOOT GATE, AND THE ONE THAT CANNOT BE FIXED BY A TIMEOUT ALONE.
   *
   * `isContextLoading` is four conditions, and two of them are DATA, not flags: `!user` and
   * `!currentTenant`. Clearing `tenantLoading` on a timeout would move the spinner from
   * "Loading your workspace..." to "Loading user data..." and change nothing, because there
   * is no honest tenant to invent. So this gate gives up and says so instead of pretending.
   *
   * It also covers a real hang in PermissionContext that no timeout of its own would catch:
   * its effect cleanup aborts the in-flight fetch, the `finally` deliberately skips
   * `setLoading(false)` when `signal.aborted` (a successor normally clears it), and the
   * re-run then takes the branch that deliberately KEEPS `loading` true "while waiting for
   * conditions to be met". If `currentTenant` never arrives, those conditions never come and
   * permissions stay loading forever. Both remaining gates fail into this same screen, which
   * is why there is one watchdog here rather than two more scattered through the contexts.
   *
   * The budget is shared with the gates above (AuthContext, ProtectedRouteWithResetCheck), so
   * the whole cascade gives up at one instant rather than adding up. See sessionBoot.ts.
   */
  const [bootGaveUp, setBootGaveUp] = useState(false);

  useEffect(() => {
    if (!isContextLoading) {
      setBootGaveUp(false);
      return;
    }
    const budgetMs = SESSION_BOOT_WATCHDOG_MS;
    const watchdog = setTimeout(() => {
      console.warn(
        `[boot] the workspace did not finish loading within ${budgetMs}ms ` +
          `(auth=${authLoading} tenant=${tenantLoading} permissions=${permissionLoading} ` +
          `user=${Boolean(user)} tenant_loaded=${Boolean(currentTenant)}); ` +
          'showing a retry instead of spinning forever. See sessionBoot.ts.'
      );
      setBootGaveUp(true);
    }, budgetMs);
    return () => clearTimeout(watchdog);
  }, [isContextLoading, authLoading, tenantLoading, permissionLoading, user, currentTenant]);

  if (isContextLoading && bootGaveUp) {
    return <WorkspaceUnavailable />;
  }

  // Show loading state while contexts are initializing
  if (isContextLoading) {
    return <PageLoadingSpinner message="Loading your workspace..." />;
  }

  // Ensure all required context data is available
  if (!user || !currentTenant) {
    return <PageLoadingSpinner message="Loading user data..." />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Notification Permission Banner */}
      <div className="relative z-[100]">
        <NotificationPermissionBanner />
      </div>

      {/* Fixed Header */}
      <Header toggleSidebar={toggleSidebar} />

      {/* Impersonation Banner */}
      {isImpersonating && (
        <ImpersonationBanner
          sidebarOpen={sidebarOpen}
          sidebarCollapsed={sidebarCollapsed}
        />
      )}

      {/* Content area with padding for fixed header and banner */}
      <div className={`flex flex-1 ${isImpersonating ? "pt-24" : "pt-14"}`}>
        {/*
          Overlay for the off-canvas sidebar.

          NAV-4.2 REMOVED AN `isMobile` GATE FROM HERE, and that was a bug fix rather than a cleanup. The
          element is already `md:hidden`, so CSS answers the viewport question — the device check was a
          second, redundant gate that could only ever WRONGLY SUPPRESS the overlay: on a narrow desktop
          window (< md) the sidebar slid over the content with no backdrop, so there was nothing to click to
          dismiss it. Device detection was never the right signal for a layout question; it answers whether
          `100vh` lies, and nothing else.
        */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[100] md:hidden"
            onClick={toggleSidebar}
          />
        )}

        {/* Fixed Sidebar */}
        <div
          className={`transform transition-transform duration-200 ease-in-out fixed top-14 bottom-0 left-0 ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0 z-[100]`}
          style={{
            height: isMobile
              ? "calc(var(--vh, 1vh) * 100 - 3.5rem)"
              : "calc(100vh - 3.5rem)"
          }}
        >
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={toggleSidebarCollapse}
            onOpenAddUser={() => setIsAddUserPanelOpen(true)}
          />
        </div>

        {/* Main content with margin for sidebar */}
        {/*
          The sidebar is fixed, so the page is offset by a margin. That margin was a
          hardcoded `md:ml-56` mirroring a fixed `w-56`; the rail now sizes to its
          longest label and publishes `--app-sidebar-w`, so the offset follows it.
          This stays a `md:` breakpoint rather than keying off `isMobile`. NAV-4.2 fixed
          the staleness this comment used to cite as the reason — the flag updates now —
          but the conclusion is unchanged and is in fact firmer: the margin depends on
          whether the sidebar is beside the content, which is a VIEWPORT question, and CSS
          answers it without a second source of truth to keep in step.
        */}
        <main
          className={`pl-3 pr-4 pt-4 flex-1 min-w-0 transition-all duration-300 ml-0 ${
            sidebarOpen ? "md:ml-[var(--app-sidebar-w,14rem)]" : ""
          }`}
          style={{
            overflowX: "hidden",
            // On mobile, use CSS variable --vh for accurate viewport height
            // On desktop, use standard vh units
            ...(isMobile ? {
              minHeight: isImpersonating ? "calc(var(--vh, 1vh) * 100 - 6rem)" : "calc(var(--vh, 1vh) * 100 - 3.5rem)",
              height: "auto",
              maxHeight: "none"
            } : {
              height: isImpersonating ? "calc(100vh - 6rem)" : "calc(100vh - 3.5rem)",
              maxHeight: isImpersonating ? "calc(100vh - 6rem)" : "calc(100vh - 3.5rem)"
            }),
            overflowY: "auto",
            display: "flex",
            flexDirection: "column"
          }}
        >
          {/*
            NAV-6.1: keyed error reset + lazy Suspense live HERE, not above
            <Routes>. Pathname change remounts only the page; Header and Sidebar
            stay mounted. A first-visit lazy /app chunk suspends into this
            content-area fallback instead of the outer App.tsx Suspense.
          */}
          <RouteErrorBoundary>
            <Suspense
              fallback={
                outletSkeleton?.family === "commission" ? (
                  <CommissionDestinationSkeleton kind={outletSkeleton.kind} />
                ) : outletSkeleton ? (
                  <OutletDestinationSkeleton kind={outletSkeleton.kind} />
                ) : (
                  <SectionLoadingSpinner message="Loading..." />
                )
              }
            >
              <Outlet />
            </Suspense>
          </RouteErrorBoundary>
        </main>
      </div>

      {/*
        ⚠️ FOUND, NOT FIXED (NAV-4.2): THIS IS A SECOND BACKDROP AND IT IS DEAD.

        It is identical to the one above — same classes bar the z-index, same click handler — but at `z-30`
        it sits BEHIND the sidebar (`z-[100]`) and behind the overlay above it, so it is never the element
        the user clicks and contributes nothing visible. One of the two should go.

        Not removed here because this story is scoped to the stale flag, and deleting a backdrop is a visual
        change that wants checking in a browser against an actually-open mobile sidebar — which is NAV-4.1's
        territory, not a blind edit. The `isMobile` gate is dropped for the same reason as above so the two
        stay consistent while both exist.
      */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={toggleSidebar}
        />
      )}

      <Toaster />
      
      {/* Global Project Modal - listens for openProjectModal events */}
      <FollowedProjectsModal />
      
      {/* AI chat: V1/n8n retired. Temporarily limited to superadmins while the
          OpenAI key is updated + V2 is validated; widen this gate to re-enable
          for all users. Hidden on Tracking and Chat pages. */}
      {!location.pathname.includes("/tracking") &&
        !location.pathname.includes("/chat") &&
        hasPermission('system.override') && (
          <AiChatWidgetV2 />
        )}

      {/* Add User Slide Panel */}
      <SlidePanel
        isOpen={isAddUserPanelOpen}
        onClose={() => setIsAddUserPanelOpen(false)}
        title="Add New User"
        width="lg"
        showEdgeCloseButton={true}
        showCloseButton={false}
        topOffset="3.5rem"
      >
        <AddUserPage onClose={() => setIsAddUserPanelOpen(false)} />
      </SlidePanel>

      {/* Update notification. Sits behind the sidebar and slides out from under it;
          it reads the rail's width from --app-sidebar-w itself, so there is no
          sidebar state to pass down here. */}
      {showNotification && (
        <UpdateFloatingCard
          onRefresh={handleRefresh}
          onDismiss={handleDismiss}
        />
      )}
    </div>
  );
}
